import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import BaseRouter from './components/base-router'
import conductor from 'scoreboard/components/conductor'

// important: instantiating this creates all our routers
import metadataObjects from 'scoreboard/components/metadata-objects'

import appDisplay from 'scoreboard/components/app-display'
import globals from 'scoreboard/components/globals'
import i18n from 'scoreboard/components/i18n'
import NoObject from 'scoreboard/components/no-object'
import briefer from './briefings/briefer'
import spectator from 'scoreboard/components/spectator'
import EmptyState from 'scoreboard/components/empty-state'
import HttpStatusCodes from 'http-status-codes'
import dialogStack from 'scoreboard/components/dialog-stack'
import guard from 'scoreboard/components/unsaved/guard'
import noPermission from 'scoreboard/components/no-permission.hbs'
import sections from 'scoreboard/components/sections'
import notFound from 'scoreboard/components/not-found.hbs'
import { getBaseUrl } from 'scoreboard/components/scoreboard-url'
import SharedDashboard from 'scoreboard/dashboards/shared-dashboard'
import displayErrorHandler from 'scoreboard/components/display-error-handler'
import Dataset from 'scoreboard/datasets/model/dataset'
import noPermissionSpecificMessage from 'scoreboard/components/no-permission-specific-message'
import layoutManager from 'scoreboard/components/layout-manager'
import menuManager from 'scoreboard/menu-manager'

/**
 * @extends BaseRouter
 */
var AppRouter = BaseRouter.extend(/** @lends AppRouter.prototype */{

	routes: {
		'organizations/:id/:section(/)': 'overview'
	},

	initialize: function() {
		BaseRouter.prototype.initialize.apply(this, arguments)
		$(document).on('click', '.sp-navigate', (e) => {
			if (!e.metaKey) {
				e.preventDefault()

				var href = $(e.currentTarget).attr('href')
				if (href) {
					// Could be a fully qualified but internal URL, so strip
					// off the baseUrl so the Backbone router can handle it
					href = href.replace(getBaseUrl(), '')
				}

				var m = metadataObjects[conductor.section()]

				// certain sections may contain additional conditions on when
				// to apply guards
				if (_.invoke(m, 'router.preventGuard', href)) {
					this.goTo(href)
				} else {
					guard.conditionally({
						continueFunction: () => {
							this.goTo(href)
							if (href.startsWith('admin/') || href.startsWith('/admin/')) {
								// #55855 need to manually hide the org pane because
								// menuManager doesn't do it when we're clicking in
								// a dialog.
								menuManager.hideOrganizationPane()
							}
						}
					})
				}
			}
		})
	},

	/**
	 * Goto the given href, which may be relative to the current path.  (note
	 * this is copied from dash)
	 */
	goTo: function (href) {
		if (href === null || _.isUndefined(href)) {
			throw new Error('href must be specified')
		}

		const {dialog} = dialogStack.findDialogWithView(view => view.awaitConfirmation)
		if (dialog) {
			dialog.once('close', () => this.goTo(href))
		} else {
			this._doNavigate(href)
		}
	},

	_doNavigate: function (href) {

		var oldFragment = Backbone.history.fragment

		if (conductor.isSharedDashboardDrilldown(href)) { // #25052
			window.open(SharedDashboard.drilldownUrl(href))
		} else {
			const newSection = conductor.section(href)
			const sameSection = newSection === conductor.section(oldFragment)
			if (isDatasetOverviewDrilldown(href, sameSection, newSection)) {
				Backbone.trigger(Dataset.DRILLDOWN, conductor.id(href))
			} else {
				// navigating from a new domain object or folder to a section overview
				// where no items exist in the tree yet. Since we don't have routes
				// for `forms/`, `dashboards/`, etc. and depend on the
				// app-router#overview behavior below we must reconstruct the
				// route as if they clicked on a section in the nav (#35432)
				if (href === `${newSection}/`) {
					href = `organizations/${globals.orgId}/${href}`
				}
				// Tabs use sp-navigate; so if we're not changing objects this
				// isn't a drilldown
				if (this._isDrilldown(href, newSection, sameSection)) {
					conductor.drilldown(href, { trigger: true })
				} else {
					conductor.navigate(href, { trigger: true })
				}
				/*
				 Backbone does not load a URL (fragment) if it matches current one.

				 However, we want the view to be reloaded (Issue #9722), so we do that here

				 Backbone.history.fragment is used everywhere because it will change when
				 a different view is loaded.  But when it is the same we want to rely on
				 the Backbone url normalization and not use the href here
				 */
				if (oldFragment === Backbone.history.fragment) {
					Backbone.history.loadUrl(Backbone.history.fragment)
				}
			}
			dialogStack.clear()
		}
	},

	_isDrilldown: function(href, newSection, isSameSection) {
		return !(
			this._isSameObject(href, isSameSection) ||
			// #36022 These sections use sp-navigate (unlike the rest)
			// and so we must not do a drill down when user clicks sections
			// in the nav menu.
			_.includes(['home','admin', 'bookmarks', 'test'], newSection)
		)
	},

	/**
	 * @returns {boolean} true if the new url is representative of the same domain
	 * object the user is currently viewing.  Normally this is just a match on
	 * conductor.id(), but forms really complicates it (read below)
	 */
	_isSameObject: function(href, isSameSection) {
		const newId = conductor.id(href)
		// In overview mode Forms hide pages in the nav, but when the edit
		// tab is clicked we show them and navigate to whatever page they
		// were viewing. appDisplay.domainObjectId() is needed here to
		// indicate that we are viewing the same logical object and this
		// is NOT a drilldown which would have adverse affects on how
		// the nav is updated.
		const sameObject = isSameSection && (
			conductor.id() == newId || // Most common scenario
			conductor.id() == appDisplay.domainObjectId() || // Forms nav
			newId == appDisplay.domainObjectId() // Forms edit tab
		)
		return sameObject
	},

	overview: function(orgId, sectionFromUrl) {
		var urlSection = sectionFromUrl
		var actualSection = sections.fromUrl(sectionFromUrl)

		if (!spectator.getCurrent()) {
			globals.orgId = orgId
		}

		if (actualSection === 'scorecards' || actualSection === 'dashboards') {
			if (this.handleNoCalendars({heading: actualSection == 'scorecards' ?
				i18n.get('scorecards.scorecards') : i18n.get('dashboards.dashboards')})) {
				return
			}
		}

		// #54374 if forms or datasets are restricted by license, display
		// a message advertising the feature
		if (actualSection === 'datasets' || actualSection == 'forms') {
			if (this.handleLicenseRestriction({section: actualSection})) {
				return
			}
		} else if (layoutManager.navAutoCollapsed) {
			// If the nav is collapsed, check if it was auto collapsed due
			// to a license restriction. If so, expand it back to normal.
			// Otherwise, respect the user's manual collapse choice.
			layoutManager.toggleNavCollapsed(false)
		}

		var m = metadataObjects[actualSection]
		// fixes #4262 not all data loaded when switching sections
		var data = _.extend({
			calendarPeriodId: globals.periodId,
			datasets: true,
			subscriptions: true // #33481
		}, _.result(m, 'loadOptions'))

		var url = 'api/organizations/' + orgId + '/' + urlSection
		if (_.includes(['dashboards', 'strategyMaps'], actualSection)) {
			url += '?widgets=true'
		}

		var model = new m.model()
		var modelPromise = model.fetch({
			url: url,
			data: data,
			error: function(model, response, options) {
				// if there is nothing in the organization
				if (response.status == HttpStatusCodes.NOT_FOUND) {
					let resData = response.responseJSON || {}
					if (resData.empty) {
						appDisplay.display(new NoObject({ section: actualSection }))
						if (model.section === 'briefings') {
							briefer.toggleBriefingLayout(false)
						}
						appDisplay.navView().toggleTree(false, { organizationTemplateId: resData.organizationTemplateId })
					} else {
						appDisplay.display(new EmptyState({ body: notFound(resData) }))
					}
				} else if (response.status == HttpStatusCodes.FORBIDDEN) {
					// #41667 build a specific message, if applicable
					let messageWithLink = noPermissionSpecificMessage(_.get(response, 'responseJSON'))
					appDisplay.display(new EmptyState({ body: noPermission({messageWithLink}), response }))
				} else {
					// If there's some other error loading the model, we need to make sure
					// the display pane is mobile ready since there's nothing to display
					displayErrorHandler(response)
					appDisplay.display(new EmptyState())
				}
			}
		})
		_.invoke(m.router, 'beforeOverviewDisplay')

		appDisplay.display(modelPromise, function() {
			const nav = appDisplay.navView()
			// fix the URL first b/c some screens depend on it being correct
			const url = `${urlSection}/${model.id}${nav.awaitingEdit ? '/edit' : ''}`
			conductor.navigate(url, { replace: true, trigger: nav.awaitingEdit })
			return !nav.awaitingEdit && m.router.overviewWithModel(model)
		}.bind(this))

		let options = {orgId, autoSelectRoot: true}
		appDisplay.nav(m.navView, options).then(nav => {
			nav.toggleEdit(false)
			nav.show(options)
			_.invoke(nav, 'removeTransient')
		})
	}
})

function isDatasetOverviewDrilldown(href, sameSection, newSection) {
	return !sameSection && newSection === 'datasets' &&
		!!href.match(/datasets\/\d+\/?$/)
}

export default AppRouter
export { isDatasetOverviewDrilldown }

AppRouter.__filename__ = 'app-router.js'
